import { useEffect, useState } from "react";
import { Scale } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const eur = (v) => `${v > 0 ? "+" : v < 0 ? "−" : ""}€${Math.abs(Number(v || 0)).toFixed(2)}`;
const tone = (v) => (v > 0 ? "text-green-600" : v < 0 ? "text-red-500" : "text-slate-600");

/** Membership value in EUR: ticket discounts (vs full price) minus membership fees paid. */
export function MembershipValueCard({ memberId }) {
  const { authAxios } = useAuth();
  const thisYear = String(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [year, setYear] = useState(thisYear);

  useEffect(() => {
    authAxios.get("/fa-analytics/membership-value", { params: { member_id: memberId || undefined } })
      .then(r => setData(r.data)).catch(() => setData(false));
  }, [memberId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (data === false) return null;
  const years = data ? [...new Set([thisYear, ...Object.keys(data.years)])].sort().reverse() : [thisYear];
  const empty = { fees: 0, discounts: 0, value: 0, members: 0, average: 0 };
  const s = !data ? empty : year === "all" ? data.all : (data.years[year] || empty);
  const testid = memberId ? "member-value" : "membership-value";

  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm mb-4" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <h3 className="font-bold text-sm text-[#1e3a5f] flex items-center gap-2"><Scale className="w-4 h-4" /> Membership value</h3>
        <div className="flex gap-1 flex-wrap">
          {[...years, "all"].map(y => (
            <button key={y} onClick={() => setYear(y)} data-testid={`${testid}-year-${y}`}
              className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-colors ${year === y ? "bg-[#1e3a5f] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {y === "all" ? "All time" : y}
            </button>
          ))}
        </div>
      </div>
      <div className={`grid gap-2 text-center ${memberId ? "grid-cols-3" : "grid-cols-2 sm:grid-cols-4"}`}>
        <div className="bg-slate-50 rounded-xl p-2.5"><p className={`text-base sm:text-lg font-extrabold whitespace-nowrap ${tone(s.value)}`} data-testid={`${testid}-total`}>{eur(s.value)}</p><p className="text-[10px] text-slate-500">{memberId ? "Value" : "Total value"}</p></div>
        {!memberId && <div className="bg-slate-50 rounded-xl p-2.5"><p className={`text-lg font-extrabold ${tone(s.average)}`} data-testid={`${testid}-average`}>{eur(s.average)}</p><p className="text-[10px] text-slate-500">Average per member ({s.members})</p></div>}
        <div className="bg-green-50 rounded-xl p-2.5"><p className="text-lg font-bold text-green-600" data-testid={`${testid}-discounts`}>€{s.discounts.toFixed(2)}</p><p className="text-[10px] text-slate-500">Ticket discounts</p></div>
        <div className="bg-amber-50 rounded-xl p-2.5"><p className="text-lg font-bold text-amber-600" data-testid={`${testid}-fees`}>€{s.fees.toFixed(2)}</p><p className="text-[10px] text-slate-500">Fees paid</p></div>
      </div>
      <p className="text-[10px] text-slate-400 mt-2">Discounts on tickets compared to the full price, minus membership fees paid. Positive = members got more back than they paid.</p>
    </div>
  );
}
